import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { v4 as uuid } from "uuid";

export default function AttHolidaysList({ selectedDevice }) {
  /*   All States
   ********************************************* */
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [holidaysList, setHolidaysList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [newHolidayForm, setNewHolidayForm] = useState({
    holiday: "",
    name: "",
  });

  /*   All Functions
   ********************************************* */
  const dispatch = useDispatch();
  // Fetch holidays from database
  const fetchHolidays = async () => {
    if (!selectedDevice) {
      dispatch(
        setNotification({
          message: "Please select a device first",
          type: "warning",
        })
      );
      return;
    }
    try {
      setLoading(true);
      const { data } = await API.post(
        "/device-holidays/" + selectedDevice?.id,
        { selectedYear },
        {
          headers: authHeader(),
        }
      );
      if (data.success) {
        const holidays = data.data;
        // Filter holidays by selected year

        setHolidaysList(holidays);
      } else {
        dispatch(
          setNotification({
            message: "Failed to fetch holidays",
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      dispatch(
        setNotification({
          message: error.response?.data?.message || "An error occurred",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleHolidaysListChange = (e) => {
    let id = e.target.id;
    let name = e.target.name;
    let value = e.target.value;

    setNewHolidayForm((prev) => {
      return { ...prev, [name]: value, id };
    });
  };

  // Change selected year
  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  // Add / Update Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isUpdateMode) {
      updateHoliday(selectedHoliday);
    } else {
      addHoliday();
    }
  };

  const addHoliday = async () => {
    try {
      setLoading(true);
      let { holiday } = newHolidayForm;

      if (!holiday) {
        dispatch(
          setNotification({
            message: "Please select a holiday date",
            type: "error",
          })
        );
        setLoading(false);
        return;
      }

      if (!selectedDevice?.timezone) {
        dispatch(
          setNotification({
            message: "Please select a device with timezone first",
            type: "error",
          })
        );
        setLoading(false);
        return;
      }

      // convert holiday to pakistan date time
      const date = new Date(holiday);
      const pakistanDate = new Date(
        date.toLocaleString("en-US", { timeZone: selectedDevice?.timezone })
      );
      // set hours to 0, minutes to 0, seconds to 0
      pakistanDate.setUTCHours(0, 0, 0, 0);

      // setLoading(false);

      // Format data for API
      const holidayData = {
        holiday: pakistanDate, // Format YYYY-MM-DD
        name: newHolidayForm.name,
        deviceId: selectedDevice?.id || null,
      };

      const { data } = await API.post("/device-holiday", holidayData, {
        headers: authHeader(),
      });

      if (data.success) {
        dispatch(
          setNotification({
            message: "Holiday added successfully",
            type: "success",
          })
        );
        fetchHolidays(); // Refresh the list
        setNewHolidayForm({ holiday: "", name: "" });
      } else {
        dispatch(
          setNotification({
            message: data.message || "Failed to add holiday",
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error adding holiday:", error);
      dispatch(
        setNotification({
          message: error.response?.data?.message || "An error occurred",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const updateHoliday = async (holiday) => {
    try {
      setLoading(true);
      let { holiday: holidayDate } = newHolidayForm;

      if (!holidayDate) {
        dispatch(
          setNotification({
            message: "Please select a holiday date",
            type: "error",
          })
        );
        setLoading(false);
        return;
      }

      if (!selectedDevice?.timezone) {
        dispatch(
          setNotification({
            message: "Please select a device with timezone first",
            type: "error",
          })
        );
        setLoading(false);
        return;
      }

      // convert holiday to pakistan date time
      const date = new Date(holidayDate);
      const pakistanDate = new Date(
        date.toLocaleString("en-US", { timeZone: selectedDevice?.timezone })
      );
      // set hours to 0, minutes to 0, seconds to 0
      pakistanDate.setUTCHours(0, 0, 0, 0);

      // Format data for API
      const holidayData = {
        id: holiday.id,
        holiday: pakistanDate,
        name: newHolidayForm.name,
        deviceId: selectedDevice?.id || null,
      };

      const { data } = await API.patch("/device-holiday", holidayData, {
        headers: authHeader(),
      });

      if (data.success) {
        dispatch(
          setNotification({
            message: "Holiday updated successfully",
            type: "success",
          })
        );
        fetchHolidays(); // Refresh the list
        setIsUpdateMode(false);
        setNewHolidayForm({ holiday: "", name: "" });
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to update holiday",
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error updating holiday:", error);
      dispatch(
        setNotification({
          message: error.response?.data?.message || "An error occurred",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteHoliday = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete(`/device-holiday/${id}`, {
        headers: authHeader(),
      });

      if (data.success) {
        dispatch(
          setNotification({
            message: "Holiday deleted successfully",
            type: "success",
          })
        );
        fetchHolidays(); // Refresh the list
      } else {
        dispatch(
          setNotification({
            message: "Failed to delete holiday",
            type: "error",
          })
        );
      }
    } catch (error) {
      console.error("Error deleting holiday:", error);
      dispatch(
        setNotification({
          message: error.response?.data?.message || "An error occurred",
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const parsedRecordDate = (recordTime) => {
    const date = new Date(recordTime);
    return date.toUTCString().slice(0, 16);
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      fetchHolidays();
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  if (!selectedDevice?.id || !selectedDevice?.timezone) {
    return (
      <div className="card">
        <div className="card-body">
          <p className="text-center text-muted">
            Please select a device first.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Holidays</h5>
        <div className="d-flex align-items-center">
          <label className="me-2">Year:</label>
          <select
            className="form-select form-select-sm"
            value={selectedYear}
            onChange={(e) => handleYearChange(Number(e.target.value))}
          >
            {Array.from(
              { length: 10 },
              (_, i) => new Date().getFullYear() - 5 + i
            ).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="card-body">
        <div>
          <form className="pt-2">
            <div className="d-flex gap-1 align-items-center">
              <input
                onChange={handleHolidaysListChange}
                type="text"
                name="name"
                id={newHolidayForm?.id || uuid()}
                placeholder="Enter Holiday Name"
                value={newHolidayForm?.name || ""}
                className="form-control"
              />
              <input
                onChange={handleHolidaysListChange}
                type="date"
                placeholder="Select Date"
                name="holiday"
                value={newHolidayForm?.holiday || ""}
                id={newHolidayForm?.id || uuid()}
                className="form-control"
              />
              <input
                type="submit"
                value={isUpdateMode ? "Update" : "Add"}
                onClick={handleSubmit}
                className="btn btn-success"
              />
              {isUpdateMode && (
                <button
                  onClick={(e) => {
                    setNewHolidayForm({ name: "", holiday: "" });
                    setIsUpdateMode(false);
                  }}
                  className="btn btn-danger"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>
        <hr />

        {loading ? (
          <div className="text-center ">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div
            style={{
              overflowY: "auto",
              height: "50vh",
            }}
          >
            <table className="table ">
              <thead>
                <tr className="sticky-top bg-white custom-shadow-sm">
                  <th scope="w-75 fw-bold">Holiday</th>
                  <th scope="w-75 fw-bold">Date</th>
                  <th scope="w-25 fw-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {holidaysList?.map((holiday) => (
                  <tr key={holiday.id} className="">
                    <td className="">{holiday?.name}</td>
                    <td className="">{parsedRecordDate(holiday?.holiday)}</td>
                    <td className="w-25">
                      <div className="d-flex">
                        <button
                          onClick={(e) => {
                            setIsUpdateMode(true);
                            setSelectedHoliday(holiday);
                            setNewHolidayForm({
                              holiday: holiday.holiday.substring(0, 10), // Format YYYY-MM-DD
                              name: holiday.name,
                            });
                          }}
                          className="btn btn-info btn-sm "
                          type="button"
                        >
                          <i className="mdi mdi-pencil"></i>
                        </button>
                        <button
                          onClick={(e) => {
                            deleteHoliday(holiday.id);
                          }}
                          className="btn btn-danger btn-sm ms-1"
                          type="button"
                        >
                          <i className="mdi mdi-delete"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
